import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import reactionPopSound from '../../assets/reaction-pop.mp3';

import './NewsCommentReactions.css';

const REACTIONS = [
  {
    type: 'like',
    emoji: '👍',
    label: 'Like',
  },
  {
    type: 'love',
    emoji: '❤️',
    label: 'Love',
  },
  {
    type: 'laugh',
    emoji: '😂',
    label: 'Laugh',
  },
  {
    type: 'fire',
    emoji: '🔥',
    label: 'Fire',
  },
  {
    type: 'applause',
    emoji: '👏',
    label: 'Applause',
  },
];

function NewsCommentReactions({
  reactionSummary = [],
  viewerReaction = null,
  onReact,
  disabled = false,
  compact = false,
}) {
  const [
    pickerOpen,
    setPickerOpen,
  ] = useState(false);

  const containerRef =
    useRef(null);

  const audioRef =
    useRef(null);

  useEffect(() => {
    const audio =
      new Audio(
        reactionPopSound
      );

    audio.volume = 0.2;

    audioRef.current =
      audio;

    return () => {
      if (
        audioRef.current
      ) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const playReactionSound =
    () => {
      const audio =
        audioRef.current;

      if (!audio) {
        return;
      }

      try {
        audio.currentTime = 0;

        const playPromise =
          audio.play();

        if (
          playPromise &&
          typeof playPromise.catch ===
            'function'
        ) {
          playPromise.catch(
            () => {}
          );
        }
      } catch {
        // Sound should never block
        // or break the reaction action.
      }
    };

  const normalizedSummary =
    useMemo(() => {
      if (
        !Array.isArray(
          reactionSummary
        )
      ) {
        return [];
      }

      return reactionSummary
        .filter(
          (item) =>
            item?.type &&
            Number(item?.count) > 0
        )
        .map((item) => ({
          type:
            item.type,

          count:
            Number(
              item.count || 0
            ),
        }));
    }, [reactionSummary]);

  const visibleReactions =
    useMemo(() => {
      return normalizedSummary
        .map((item) => {
          const config =
            REACTIONS.find(
              (reaction) =>
                reaction.type ===
                item.type
            );

          if (!config) {
            return null;
          }

          return {
            ...config,

            count:
              item.count,
          };
        })
        .filter(Boolean);
    }, [normalizedSummary]);

  const hasReactions =
    visibleReactions.length > 0;

  useEffect(() => {
    if (!pickerOpen) {
      return undefined;
    }

    const handlePointerDown =
      (event) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(
            event.target
          )
        ) {
          setPickerOpen(false);
        }
      };

    document.addEventListener(
      'pointerdown',
      handlePointerDown
    );

    return () => {
      document.removeEventListener(
        'pointerdown',
        handlePointerDown
      );
    };
  }, [pickerOpen]);

  useEffect(() => {
    if (disabled) {
      setPickerOpen(false);
    }
  }, [disabled]);

  const handleOpenPicker =
    () => {
      if (disabled) {
        return;
      }

      setPickerOpen(
        (current) =>
          !current
      );
    };

  const handleReaction =
    async (reactionType) => {
      if (
        disabled ||
        !reactionType
      ) {
        return;
      }

      const success =
        await onReact?.(
          reactionType
        );

      if (success !== false) {
        playReactionSound();

        setPickerOpen(false);
      }
    };

  return (
    <div
      ref={containerRef}
      className={`news-comment-reactions ${
        compact
          ? 'news-comment-reactions--compact'
          : ''
      }`}
    >
      <div className="news-comment-reactions-row">
        {visibleReactions.map(
          (reaction) => {
            const selected =
              viewerReaction ===
              reaction.type;

            return (
              <button
                key={
                  reaction.type
                }
                type="button"
                className={`news-comment-reaction-pill ${
                  selected
                    ? 'news-comment-reaction-pill--selected'
                    : ''
                }`}
                onClick={() =>
                  handleReaction(
                    reaction.type
                  )
                }
                disabled={
                  disabled
                }
                aria-label={`${reaction.label}, ${reaction.count}`}
                aria-pressed={
                  selected
                }
              >
                <span
                  className="news-comment-reaction-emoji"
                  aria-hidden="true"
                >
                  {
                    reaction.emoji
                  }
                </span>

                <span className="news-comment-reaction-count">
                  {
                    reaction.count
                  }
                </span>
              </button>
            );
          }
        )}

        <button
          type="button"
          className={`news-comment-reaction-add ${
            hasReactions
              ? 'news-comment-reaction-add--icon'
              : 'news-comment-reaction-add--text'
          }`}
          onClick={
            handleOpenPicker
          }
          disabled={
            disabled
          }
          aria-label={
            hasReactions
              ? 'Add reaction'
              : 'React'
          }
          aria-expanded={
            pickerOpen
          }
        >
          {hasReactions
            ? '+'
            : 'React'}
        </button>
      </div>

      {pickerOpen && (
        <div
          className="news-comment-reaction-picker"
          role="menu"
          aria-label="Choose a reaction"
        >
          {REACTIONS.map(
            (reaction) => {
              const selected =
                viewerReaction ===
                reaction.type;

              return (
                <button
                  key={
                    reaction.type
                  }
                  type="button"
                  className={`news-comment-reaction-picker-btn ${
                    selected
                      ? 'news-comment-reaction-picker-btn--selected'
                      : ''
                  }`}
                  onClick={() =>
                    handleReaction(
                      reaction.type
                    )
                  }
                  disabled={
                    disabled
                  }
                  aria-label={
                    reaction.label
                  }
                  title={
                    reaction.label
                  }
                >
                  <span
                    aria-hidden="true"
                  >
                    {
                      reaction.emoji
                    }
                  </span>
                </button>
              );
            }
          )}
        </div>
      )}
    </div>
  );
}

export default NewsCommentReactions;
